import React, { useState } from 'react';
import { api, formatApiError } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { CalendarBlank, Plus, Trash, ArrowUp, ArrowDown } from '@phosphor-icons/react';
import { toast } from 'sonner';

const FREQUENCIES = [
  { value: 'one-off', label: 'One-off (single date)' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annually', label: 'Annually' },
];

const ROW_TYPES = [
  { type: 'heading', label: 'Section' },
  { type: 'text_input', label: 'Text' },
  { type: 'date_input', label: 'Date' },
  { type: 'yes_no', label: 'Yes/No' },
  { type: 'checklist', label: 'Checkbox' },
  { type: 'paragraph', label: 'Note' },
];

export const ScheduleReminderDialog = ({
  open, onOpenChange, clientId, type, templateId, templateName,
  initialFields, documentPath, documentName, onCreated,
}) => {
  const [frequency, setFrequency] = useState('quarterly');
  const [nextDate, setNextDate] = useState('');
  const [formName, setFormName] = useState(templateName || '');
  const [fields, setFields] = useState(initialFields || []);
  const [saving, setSaving] = useState(false);

  const resetAndOpen = () => {
    setFormName(templateName || documentName || '');
    setFields(initialFields ? JSON.parse(JSON.stringify(initialFields)) : []);
    setFrequency('quarterly');
    setNextDate('');
  };

  React.useEffect(() => {
    if (open) resetAndOpen();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateField = (i, updated) => { const f = [...fields]; f[i] = updated; setFields(f); };
  const removeField = (i) => setFields(fields.filter((_, idx) => idx !== i));
  const moveField = (from, to) => { const f = [...fields]; const [m] = f.splice(from, 1); f.splice(to, 0, m); setFields(f); };
  const addField = (ftype) => {
    const id = `field_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    setFields([...fields, { id, type: ftype, ...(ftype === 'heading' || ftype === 'paragraph' ? { content: '' } : { label: '' }) }]);
  };

  const handleSave = async () => {
    if (!nextDate) { toast.error('Please set the first run date'); return; }
    setSaving(true);
    try {
      const payload = {
        type,
        template_id: templateId || '',
        template_name: templateName || '',
        frequency,
        next_run_date: nextDate,
        fields: type === 'form' ? fields : [],
        form_name: type === 'form' ? formName : '',
        document_name: type === 'document' ? (documentName || formName) : '',
        document_path: documentPath || '',
      };
      await api.post(`/clients/${clientId}/scheduled-reminders`, payload);
      toast.success(`Scheduled ${frequency} reminder set for ${nextDate}`);
      onOpenChange(false);
      if (onCreated) onCreated();
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="flex items-center gap-2"><CalendarBlank className="w-5 h-5" /> Schedule Recurring Reminder</DialogTitle>
          <DialogDescription>This {type} will be automatically shared to the client's portal on the scheduled dates</DialogDescription>
        </DialogHeader>
        <div className="px-6 pb-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-sm">Frequency</Label>
              <Select value={frequency} onValueChange={setFrequency}>
                <SelectTrigger data-testid="reminder-frequency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FREQUENCIES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-sm">First Run Date</Label>
              <Input type="date" value={nextDate} onChange={(e) => setNextDate(e.target.value)} data-testid="reminder-date" />
            </div>
          </div>

          {type === 'form' && (
            <>
              <div className="space-y-1">
                <Label className="text-sm">Form Name</Label>
                <Input value={formName} onChange={(e) => setFormName(e.target.value)} data-testid="reminder-form-name" />
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Form Fields (edit labels and pre-fill dates for the next run)</Label>
                <div className="bg-gray-50 rounded-lg p-3 border max-h-[320px] overflow-y-auto">
                  {fields.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-4">No fields — add rows below</p>
                  ) : (
                    <table className="w-full border-collapse text-sm">
                      <tbody>
                        {fields.map((field, idx) => {
                          const isContent = field.type === 'heading' || field.type === 'paragraph';
                          return (
                            <tr key={field.id || idx} className="group">
                              <td className={`border border-gray-300 px-3 py-2 ${isContent ? 'bg-gray-100' : 'bg-white w-[45%]'}`} colSpan={isContent ? 3 : 1}>
                                <input value={isContent ? (field.content || '') : (field.label || '')} onChange={(e) => updateField(idx, { ...field, [isContent ? 'content' : 'label']: e.target.value })} placeholder={isContent ? 'Section heading...' : 'Field label...'} className={`w-full bg-transparent focus:outline-none ${isContent ? 'font-bold uppercase text-xs tracking-wide' : 'text-sm'}`} />
                              </td>
                              {!isContent && (
                                <td className="border border-gray-300 px-3 py-2 bg-white">
                                  <input value={field.prefill || ''} onChange={(e) => updateField(idx, { ...field, prefill: e.target.value })} placeholder={field.type === 'date_input' ? 'e.g. 5 April 2026' : 'Pre-fill (optional)...'} className="w-full bg-transparent text-sm text-gray-500 focus:outline-none" />
                                </td>
                              )}
                              <td className="border border-gray-300 px-1 py-1 w-[60px] text-center">
                                <div className="flex items-center justify-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                  {idx > 0 && <button type="button" onClick={() => moveField(idx, idx - 1)} className="p-0.5"><ArrowUp className="w-3 h-3 text-gray-400" /></button>}
                                  {idx < fields.length - 1 && <button type="button" onClick={() => moveField(idx, idx + 1)} className="p-0.5"><ArrowDown className="w-3 h-3 text-gray-400" /></button>}
                                  <button type="button" onClick={() => removeField(idx)} className="p-0.5"><Trash className="w-3 h-3 text-red-400" /></button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {ROW_TYPES.map((rt) => (
                    <Button key={rt.type} type="button" variant="outline" size="sm" className="text-xs h-7" onClick={() => addField(rt.type)}>
                      <Plus className="w-3 h-3 mr-1" /> {rt.label}
                    </Button>
                  ))}
                </div>
              </div>
            </>
          )}

          {type === 'document' && (
            <div className="p-4 bg-muted rounded-md text-sm">
              <p><strong>Document:</strong> {documentName || 'Unnamed'}</p>
              <p className="text-muted-foreground mt-1">This document will be re-shared to the client's portal on each scheduled date.</p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-3 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-reminder-btn">
              {saving ? 'Saving...' : 'Create Schedule'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
